import type { DominoRuleSet } from './types.js';
import { ALL_FIVES, ALL_FIVES_PARTNERSHIP } from './allFives.js';
import { TELEFON, TELEFON_PARTNERSHIP } from './telefon.js';
import { GOAT, GOAT_PARTNERSHIP } from './goat.js';

export type VariantId =
  | 'goat'
  | 'goat-2v2'
  | 'all-fives'
  | 'all-fives-2v2'
  | 'telefon'
  | 'telefon-2v2';

export const VARIANTS: Record<VariantId, DominoRuleSet> = {
  goat: GOAT,
  'goat-2v2': GOAT_PARTNERSHIP,
  'all-fives': ALL_FIVES,
  'all-fives-2v2': ALL_FIVES_PARTNERSHIP,
  telefon: TELEFON,
  'telefon-2v2': TELEFON_PARTNERSHIP,
};

const VARIANT_IDS = Object.keys(VARIANTS) as readonly VariantId[];

/**
 * Ids that shipped before a rename and still live in stored data (settings,
 * active game, local + server history, room checkpoints) and in cached old
 * clients. `kozyol` meant Телефон; it is reserved forever and must never be
 * reused for another variant, or old records would replay with wrong rules.
 */
export const LEGACY_VARIANT_ALIASES: Readonly<Record<string, VariantId>> = {
  kozyol: 'telefon',
  'kozyol-2v2': 'telefon-2v2',
};

export function isVariantId(value: unknown): value is VariantId {
  return typeof value === 'string' && (VARIANT_IDS as readonly string[]).includes(value);
}

/** Resolve a variant id read from outside the running code; `null` when unknown. */
export function normalizeVariantId(raw: unknown): VariantId | null {
  if (isVariantId(raw)) return raw;
  if (typeof raw !== 'string' || !Object.hasOwn(LEGACY_VARIANT_ALIASES, raw)) return null;
  return LEGACY_VARIANT_ALIASES[raw] ?? null;
}

export function getRuleSet(id: VariantId): DominoRuleSet {
  return VARIANTS[id];
}

/** Per-game values the user (Settings, room options, a record's seed) may override. */
export interface RuleOverrides {
  readonly pointGoal?: number | undefined;
  readonly mugginsStrict?: boolean | undefined;
  /** Козёл only: the 13-point opening. */
  readonly opening13?: boolean | undefined;
  /** Козёл only: the «Лысый» finish. */
  readonly baldFinish?: boolean | undefined;
}

/**
 * Apply user overrides to a variant's rule set. Penalty variants (Козёл) have
 * a fixed 101 match and no Muggins, so `pointGoal` / `mugginsStrict` are
 * ignored there — a stale Settings `pointGoal` of 150 must never change what
 * loses the match. Conversely the Козёл toggles never reach a fives variant.
 */
export function applyRuleOverrides(base: DominoRuleSet, overrides: RuleOverrides): DominoRuleSet {
  if (base.scoring === 'penalty') {
    return {
      ...base,
      opening13: overrides.opening13 ?? base.opening13,
      baldFinish: overrides.baldFinish ?? base.baldFinish,
    };
  }
  return {
    ...base,
    pointGoal: overrides.pointGoal ?? base.pointGoal,
    mugginsStrict: overrides.mugginsStrict ?? base.mugginsStrict,
  };
}

export {
  ALL_FIVES,
  ALL_FIVES_PARTNERSHIP,
  TELEFON,
  TELEFON_PARTNERSHIP,
  GOAT,
  GOAT_PARTNERSHIP,
};
export type { DominoRuleSet };
